import { Injectable, signal } from '@angular/core';

export interface NavigationContext {
  fromTaskDetail?: boolean;
  fromPatientsList?: boolean;
  fromDrugReactionDetail?: boolean;
  taskId?: string;
  drugReactionId?: string;
  allowHospitalAccess?: boolean;
  timestamp?: number;
}

@Injectable({
  providedIn: 'root'
})
export class NavigationTrackingService {
  private readonly STORAGE_KEY = 'ezfy_navigation_context';
  private readonly CONTEXT_TIMEOUT = 5 * 60 * 1000; // 5 minutes

  private readonly navigationContext = signal<NavigationContext | null>(null);

  /**
   * Set navigation context when navigating from task detail to patient
   */
  setNavigationContext(context: NavigationContext): void {
    const contextWithTimestamp = {
      ...context,
      timestamp: Date.now()
    };
    
    this.navigationContext.set(contextWithTimestamp);
    
    // Store in sessionStorage to persist across page refreshes
    sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(contextWithTimestamp));
  }

  /**
   * Get current navigation context
   */
  getNavigationContext(): NavigationContext | null {
    let context = this.navigationContext();
    
    // If not in memory, try to get from sessionStorage
    if (!context) {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        try {
          context = JSON.parse(stored);
          this.navigationContext.set(context);
        } catch (error) {
          console.warn('Failed to parse navigation context from storage:', error);
          this.clearNavigationContext();
          return null;
        }
      }
    }

    // Check if context has expired
    if (context && context.timestamp) {
      const isExpired = Date.now() - context.timestamp > this.CONTEXT_TIMEOUT;
      if (isExpired) {
        this.clearNavigationContext();
        return null;
      }
    }

    return context;
  }

  /**
   * Check if hospital user has valid access to patient page
   */
  hasValidHospitalAccess(): boolean {
    const context = this.getNavigationContext();
    return !!(context?.allowHospitalAccess && (context?.fromTaskDetail || context?.fromPatientsList || context?.fromDrugReactionDetail));
  }

  /**
   * Clear navigation context (called when leaving patient page or on timeout)
   */
  clearNavigationContext(): void {
    this.navigationContext.set(null);
    sessionStorage.removeItem(this.STORAGE_KEY);
  }

  /**
   * Update navigation context when router state is available
   */
  updateFromRouterState(routerState: any): void {
    if (routerState?.fromTaskDetail || routerState?.fromPatientsList || routerState?.fromDrugReactionDetail) {
      this.setNavigationContext({
        fromTaskDetail: routerState.fromTaskDetail,
        fromPatientsList: routerState.fromPatientsList,
        fromDrugReactionDetail: routerState.fromDrugReactionDetail,
        taskId: routerState.taskId,
        drugReactionId: routerState.drugReactionId,
        allowHospitalAccess: routerState.allowHospitalAccess
      });
    }
  }
}
